import { ReactNode, useEffect, useMemo, useState } from 'react';

// material-ui
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// third-party
import dayjs from 'dayjs';

// project imports
import MainCard from '@/components/MainCard';
import IconButton from '@/components/@extended/IconButton';
import Dot from '@/components/@extended/Dot';
import TruncatedText from '@/components/TruncatedText';
import { TNUM_SX } from '@/lib/format';
import { BackendInfo, fetchBackendInfo } from '@/services/info';

// assets
import DesktopOutlined from '@ant-design/icons/DesktopOutlined';
import DatabaseOutlined from '@ant-design/icons/DatabaseOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';

// ==============================|| DRAWER - VERSION INFO ||============================== //

interface VersionItem {
	icon: ReactNode;
	label: string;
	version: string;
	date: string;
}

const formatDate = (value?: string) =>
	value ? dayjs(value).format('DD.MM.YYYY, HH:mm') : '—';

// `mini`: varianta pentru meniul restrâns — doar indicatorul, cu TOATE datele în tooltip.
export default function VersionInfo({ mini = false }: { mini?: boolean }) {
	const [open, setOpen] = useState(false);
	const [backendInfo, setBackendInfo] = useState<BackendInfo | null>(null);

	const feVersion = __APP_VERSION__;
	const feDate = useMemo(() => formatDate(__BUILD_TIME__), []);
	const beDate = useMemo(() => formatDate(backendInfo?.buildDate), [backendInfo]);

	useEffect(() => {
		let active = true;
		fetchBackendInfo()
			.then((info) => {
				if (active) setBackendInfo(info);
			})
			.catch((error) => console.debug('Could not load backend info:', error));
		return () => {
			active = false;
		};
	}, []);

	const envLabel = backendInfo?.environment || 'CityZen';

	const items: VersionItem[] = [
		{ icon: <DesktopOutlined />, label: 'Frontend', version: feVersion, date: feDate },
		{
			icon: <DatabaseOutlined />,
			label: 'Backend',
			version: backendInfo?.version ?? '—',
			date: beDate
		}
	];

	if (mini) {
		// Tooltip „card" — aceeași structură ca varianta mare (antet env + rânduri FE/BE cu
		// iconițe), doar afișată la hover pe indicatorul din rail.
		return (
			<Tooltip
				placement="right"
				slotProps={{
					// Tooltip-ul urcă 24px față de ancoră (skidding negativ pe axa verticală).
					popper: {
						modifiers: [{ name: 'offset', options: { offset: [-48, 8] } }]
					},
					tooltip: {
						sx: {
							bgcolor: 'background.paper',
							color: 'text.primary',
							border: '1px solid',
							borderColor: 'divider',
							boxShadow: 3,
							borderRadius: 2,
							p: 1.5,
							maxWidth: 300
						}
					}
				}}
				title={
					<Stack sx={{ gap: 1.5, minWidth: 210 }}>
						<Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
							<Dot color="primary" size={10} />
							<Typography variant="subtitle2" noWrap>
								{envLabel}
							</Typography>
						</Stack>
						<Divider />
						{items.map((item) => (
							<Stack
								key={item.label}
								direction="row"
								sx={{ gap: 1.25, alignItems: 'center' }}
							>
								<Box
									sx={{
										width: 30,
										height: 30,
										flexShrink: 0,
										borderRadius: 1,
										bgcolor: 'secondary.lighter',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center'
									}}
								>
									{item.icon}
								</Box>
								<Stack sx={{ minWidth: 0 }}>
									<Typography variant="caption">
										<Box component="span" sx={{ fontWeight: 600 }}>
											{item.label}
										</Box>{' '}
										<Box component="span" sx={{ color: 'text.secondary' }}>
											{item.version}
										</Box>
									</Typography>
									<Typography
										variant="caption"
										sx={{ color: 'text.secondary', ...TNUM_SX }}
									>
										{item.date}
									</Typography>
								</Stack>
							</Stack>
						))}
					</Stack>
				}
			>
				{/* buton 36×36 cu fundal foarte fad, în ton cu iconițele din rail */}
				<Box
					aria-label="Versiuni aplicație"
					sx={(theme) => ({
						width: 36,
						height: 36,
						borderRadius: 1.5,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						bgcolor: 'primary.lighter',
						...theme.applyStyles('dark', { bgcolor: 'divider' }),
						cursor: 'default'
					})}
				>
					<Dot color="primary" size={10} />
				</Box>
			</Tooltip>
		);
	}

	return (
		<MainCard content={false} sx={{ mb: 1.5, p: 1.5 }}>
			<Stack
				direction="row"
				onClick={() => setOpen((o) => !o)}
				sx={{ gap: 1, alignItems: 'center', cursor: 'pointer' }}
			>
				<Dot color="primary" size={10} />
				<Typography variant="subtitle2" sx={{ flex: 1 }} noWrap>
					{envLabel}
				</Typography>
				<IconButton
					size="small"
					color="secondary"
					aria-label={open ? 'Restrânge' : 'Extinde'}
				>
					{open ? <UpOutlined /> : <DownOutlined />}
				</IconButton>
			</Stack>

			<Collapse in={open}>
				<Divider sx={{ my: 1.5 }} />
				<Stack sx={{ gap: 1.5 }}>
					{items.map((item) => (
						<Stack
							key={item.label}
							direction="row"
							sx={{ gap: 1.25, alignItems: 'center' }}
						>
							<Box
								sx={{
									width: 30,
									height: 30,
									flexShrink: 0,
									borderRadius: 1,
									bgcolor: 'secondary.lighter',
									color: 'text.secondary',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center'
								}}
							>
								{item.icon}
							</Box>
							<Stack sx={{ minWidth: 0, flex: 1 }}>
								<TruncatedText
									variant="caption"
									title={`${item.label} - ${item.version}`}
								>
									<Box component="span" sx={{ fontWeight: 600 }}>
										{item.label}
									</Box>{' '}
									<Box component="span" sx={{ color: 'text.secondary' }}>
										{item.version}
									</Box>
								</TruncatedText>
								<Typography
									variant="caption"
									sx={{ color: 'text.secondary', ...TNUM_SX }}
								>
									{item.date}
								</Typography>
							</Stack>
						</Stack>
					))}
				</Stack>
			</Collapse>
		</MainCard>
	);
}
